import React from "react";
import { Helmet } from "react-helmet-async";

const SITE = "The Sourcing Atlas";
const BASE = "https://thesourcingatlas.com";

export const ogUrl = (title, kicker = "The Sourcing Atlas", badge = "") =>
  `${BASE}/api/og?title=${encodeURIComponent(title)}&kicker=${encodeURIComponent(kicker)}` +
  (badge ? `&badge=${encodeURIComponent(badge)}` : "");

const ORG_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "The Sourcing Atlas",
  legalName: "The Sourcing Atlas",
  url: BASE,
  logo: `${BASE}/favicon-512.png`,
  sameAs: [
    "https://www.instagram.com/thesourcingatlas",
    "https://www.youtube.com/@thesourcingatlas",
    "https://www.linkedin.com/company/thesourcingatlas",
  ],
  description: "Independent Asian sourcing and export house helping buyers find, verify and source from manufacturing partners.",
  areaServed: ["China", "India", "Vietnam", "Thailand", "Worldwide"],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "sales",
    areaServed: ["IN", "CN", "VN", "TH", "Worldwide"],
    availableLanguage: ["en", "hi"],
  },
};

const WEBSITE_LD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE,
  url: BASE,
  potentialAction: {
    "@type": "SearchAction",
    target: `${BASE}/search?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

export default function Seo({
  title,
  description,
  path = "/",
  ogImage,
  ogType = "website",
  ogTitle,
  ogDescription,
  imageAlt = "The Sourcing Atlas — sourcing intelligence across Asia",
  jsonLd = [],
  noindex = false,
}) {
  const canonical = `${BASE}${path}`;
  const isPreview = typeof window !== "undefined" && /\.preview\.emergentagent\.com$/.test(window.location.hostname);
  const img = ogImage || `${BASE}/og-default.png`;
  const blocks = [ORG_LD, WEBSITE_LD, ...jsonLd];
  return (
    <Helmet>
      <html lang="en" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={noindex || isPreview ? "noindex,nofollow" : "index,follow,max-image-preview:large"} />
      <meta property="og:site_name" content={SITE} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:title" content={ogTitle || title} />
      <meta property="og:description" content={ogDescription || description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={img} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={img} />
      {blocks.map((b, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(b)}</script>
      ))}
    </Helmet>
  );
}

export function breadcrumbLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${BASE}${it.path}`,
    })),
  };
}

export function faqLd(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}
